import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import { getCandidateV2Options } from 'client/@tanstack/react-query.gen';

/**
 * Old address of the candidate page. The page lives at
 * `/people/:identityId` now, with the same tab paths, so every
 * `/candidates/:candidateId/...` link forwards there with its tab, query
 * string and hash kept. The routes under this one exist only so those links
 * keep their types until they are repointed.
 */
export const Route = createFileRoute(
  '/_private/_route/_internal/candidates/$candidateId',
)({
  beforeLoad: async ({ context, location, params, preload }) => {
    // Always fresh: right after a merge the cached record still looks
    // unmerged, and the forward must see the merge.
    const candidateOptions = {
      ...getCandidateV2Options({
        path: {
          candidate_uuid: params.candidateId,
        },
      }),
      staleTime: 0,
    };

    // A hover only starts the request; the click joins it. A preload must
    // never redirect: this router leaves the match it redirected from stuck,
    // and the click that follows never loads.
    if (preload) {
      void context.queryClient.prefetchQuery(candidateOptions);
      return;
    }

    const candidate = await context.queryClient.fetchQuery(candidateOptions);

    // A candidate merged into another one keeps no identity; its survivor
    // holds the data. One hop is enough: merging re-points earlier retirees
    // at each new survivor.
    if (candidate.merged_into_uuid) {
      throw redirect({
        href: location.href.replace(
          `/candidates/${params.candidateId}`,
          `/candidates/${candidate.merged_into_uuid}`,
        ),
        replace: true,
      });
    }

    if (!candidate.identity_uuid) {
      throw notFound();
    }

    // Anchored on `/candidates/<id>` so a second uuid in the path, as in
    // /candidates/<a>/compare/<b>, is left alone.
    throw redirect({
      href: location.href.replace(
        `/candidates/${params.candidateId}`,
        `/people/${candidate.identity_uuid}`,
      ),
      replace: true,
    });
  },
});
